/**
 * PlatformConfig - Platform configuration panel with toggle list for streaming destinations
 */

import React, { useState, useEffect } from 'react';
import { Platform, PLATFORM_PRESETS } from '../../lib/golive';

interface PlatformConfigProps {
  platforms: Platform[];
  onPlatformsChange: (platforms: Platform[]) => void;
  isLive: boolean;
}

interface EditModalState {
  isOpen: boolean;
  platform: Platform | null;
}

// Platform colors for icons
const PLATFORM_COLORS: Record<string, string> = {
  youtube: '#FF0000',
  twitch: '#9146FF',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  custom: '#6B7280',
};

// Platform-specific help instructions for finding stream keys
const STREAM_KEY_HELP: Record<string, { steps: string[]; link: string }> = {
  youtube: {
    steps: [
      'Go to studio.youtube.com and sign in',
      'Click "Create" then "Go live" in the top right',
      'Select "Stream" from the options',
      'Your Stream Key will be shown under "Stream Settings"',
      'Click "Copy" next to the Stream Key field',
    ],
    link: 'https://support.google.com/youtube/answer/2907883',
  },
  twitch: {
    steps: [
      'Go to twitch.tv and log into your account',
      'Click your profile in the top right corner',
      'Select "Creator Dashboard"',
      'Click "Settings" then "Stream" in the left menu',
      'Your Primary Stream Key will be displayed - click "Copy"',
    ],
    link: 'https://help.twitch.tv/s/article/twitch-stream-key-faq',
  },
  facebook: {
    steps: [
      'Go to facebook.com/live/producer',
      'Select the Page or profile you want to stream to',
      'Choose "Use stream key" option',
      'Copy the Stream Key shown in the setup',
      'Note: Use rtmps:// URL for Facebook (secure streaming)',
    ],
    link: 'https://www.facebook.com/help/587160588142067',
  },
  linkedin: {
    steps: [
      'Go to linkedin.com and click "Post" then "Go Live"',
      'You need LinkedIn Live access (apply if not enabled)',
      'Select "Use a custom stream" option',
      'Copy the Stream Key and RTMP URL provided',
      'Note: LinkedIn requires approval for live streaming',
    ],
    link: 'https://www.linkedin.com/help/linkedin/answer/100225',
  },
  custom: {
    steps: [
      'Obtain RTMP URL and Stream Key from your streaming provider',
      'Enter the full RTMP URL (usually starts with rtmp:// or rtmps://)',
      'Enter the Stream Key exactly as provided',
      'Test your connection before going live',
    ],
    link: '',
  },
};

export const PlatformConfig: React.FC<PlatformConfigProps> = ({
  platforms,
  onPlatformsChange,
  isLive,
}) => {
  // Debug: Log when component renders
  console.log('🔴🔴🔴 [PlatformConfig] COMPONENT RENDERING 🔴🔴🔴', {
    platformsCount: platforms.length,
    isLive
  });

  const [editModal, setEditModal] = useState<EditModalState>({ isOpen: false, platform: null });
  const [editForm, setEditForm] = useState({ rtmpUrl: '', streamKey: '' });
  const [showHelp, setShowHelp] = useState(false);

  // LocalStorage key for platform configs
  const STORAGE_KEY = 'golive_platform_configs';

  // Load saved platform configs from localStorage
  // Only returns platforms that have actual stream keys (filters out empty/invalid entries)
  const loadSavedConfigs = (): Record<string, { rtmpUrl: string; streamKey: string }> => {
    try {
      console.log('[PlatformConfig] DEBUG: Attempting to load from localStorage key:', STORAGE_KEY);
      const saved = localStorage.getItem(STORAGE_KEY);
      console.log('[PlatformConfig] DEBUG: Raw localStorage value:', saved);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Filter out entries with empty stream keys (cleanup old cached data)
        const filtered: Record<string, { rtmpUrl: string; streamKey: string }> = {};
        Object.entries(parsed).forEach(([key, value]) => {
          const config = value as { rtmpUrl: string; streamKey: string };
          if (config.streamKey && config.streamKey.trim()) {
            filtered[key] = config;
          }
        });
        console.log('[PlatformConfig] DEBUG: Filtered configs (only with stream keys):', filtered);
        // Save cleaned data back to localStorage
        if (Object.keys(filtered).length !== Object.keys(parsed).length) {
          console.log('[PlatformConfig] DEBUG: Cleaning up old cached data');
          localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
        }
        return filtered;
      }
      console.log('[PlatformConfig] DEBUG: No saved value found in localStorage');
    } catch (err) {
      console.error('[PlatformConfig] Failed to load saved configs:', err);
    }
    return {};
  };

  // Save platform configs to localStorage
  // Only saves platforms that have a stream key (actually configured)
  const saveConfigs = (platformList: Platform[]) => {
    try {
      const configs: Record<string, { rtmpUrl: string; streamKey: string }> = {};
      platformList.forEach(p => {
        // Only save platforms with actual stream key (not just default rtmpUrl)
        if (p.streamKey && p.streamKey.trim()) {
          configs[p.id] = { rtmpUrl: p.rtmpUrl, streamKey: p.streamKey };
        }
      });
      const jsonString = JSON.stringify(configs);
      console.log('[PlatformConfig] Saving configured platforms:', Object.keys(configs));
      localStorage.setItem(STORAGE_KEY, jsonString);
    } catch (err) {
      console.error('[PlatformConfig] Failed to save configs:', err);
    }
  };

  // Initialize all platforms on mount, restoring saved configs
  useEffect(() => {
    console.log('[PlatformConfig] useEffect running - initializing platforms');
    console.log('[PlatformConfig] Current platforms from props:', platforms.length, platforms.map(p => p.id));

    const savedConfigs = loadSavedConfigs();
    console.log('[PlatformConfig] Loaded saved configs:', Object.keys(savedConfigs), savedConfigs);

    const existingIds = platforms.map(p => p.id);
    const missingPlatforms: Platform[] = [];

    // Add standard platforms that aren't already present
    Object.entries(PLATFORM_PRESETS).forEach(([id, preset]) => {
      if (id !== 'custom' && !existingIds.includes(id)) {
        const saved = savedConfigs[id];
        console.log(`[PlatformConfig] Creating platform ${id}, saved config:`, saved);
        missingPlatforms.push({
          id,
          name: preset.name,
          icon: preset.icon,
          rtmpUrl: saved?.rtmpUrl || preset.rtmpUrl,
          streamKey: saved?.streamKey || '',
          enabled: false,
          status: 'disconnected',
        });
      }
    });

    // Add one custom RTMP if none exists
    if (!existingIds.some(id => id.startsWith('custom'))) {
      const saved = savedConfigs['custom-1'];
      missingPlatforms.push({
        id: 'custom-1',
        name: 'Custom RTMP 1',
        icon: 'fas fa-broadcast-tower',
        rtmpUrl: saved?.rtmpUrl || '',
        streamKey: saved?.streamKey || '',
        enabled: false,
        status: 'disconnected',
      });
    }

    console.log('[PlatformConfig] Missing platforms to add:', missingPlatforms.length, missingPlatforms.map(p => ({ id: p.id, hasKey: !!p.streamKey })));

    if (missingPlatforms.length > 0) {
      onPlatformsChange([...platforms, ...missingPlatforms]);
    }
  }, []);

  const handleToggle = (platformId: string) => {
    const updated = platforms.map((p) =>
      p.id === platformId ? { ...p, enabled: !p.enabled } : p
    );
    onPlatformsChange(updated);
  };

  const handleEdit = (platform: Platform) => {
    setEditForm({
      rtmpUrl: platform.rtmpUrl,
      streamKey: platform.streamKey,
    });
    setShowHelp(false); // Reset help section when opening modal
    setEditModal({ isOpen: true, platform });
  };

  const handleSaveEdit = () => {
    if (!editModal.platform) return;

    const updated = platforms.map((p) =>
      p.id === editModal.platform!.id
        ? { ...p, rtmpUrl: editForm.rtmpUrl, streamKey: editForm.streamKey }
        : p
    );
    onPlatformsChange(updated);

    // Save to localStorage for persistence
    saveConfigs(updated);
    console.log('[PlatformConfig] Configuration saved for:', editModal.platform.id);

    setEditModal({ isOpen: false, platform: null });
  };

  // Get platform color
  const getPlatformColor = (platformId: string): string => {
    if (platformId.startsWith('custom')) return PLATFORM_COLORS.custom;
    return PLATFORM_COLORS[platformId] || '#6B7280';
  };

  // Check if stream key is configured
  const isConfigured = (platform: Platform): boolean => {
    return Boolean(platform.streamKey && platform.streamKey.trim());
  };

  return (
    <div className="platform-config">
      {/* Platform List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {platforms.map((platform) => {
          const color = getPlatformColor(platform.id);
          const configured = isConfigured(platform);

          return (
            <div
              key={platform.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '14px 18px',
                background: 'var(--bg-secondary, #1a1a2e)',
                borderRadius: '12px',
                border: '1px solid var(--border-color, #333)',
              }}
            >
              {/* Platform Icon */}
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className={platform.icon} style={{ color: 'white', fontSize: '20px' }} />
              </div>

              {/* Platform Name & Status */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary, #fff)' }}>
                  {platform.name}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary, #888)', marginTop: '3px' }}>
                  Ready
                </div>
              </div>

              {/* Stream Key Status & Toggle - Stacked */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                {/* Stream Key Status */}
                <button
                  onClick={() => !isLive && handleEdit(platform)}
                  disabled={isLive}
                  style={{
                    background: configured ? 'rgba(16, 185, 129, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                    border: `1px solid ${configured ? 'rgba(16, 185, 129, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                    borderRadius: '8px',
                    padding: '6px 12px',
                    cursor: isLive ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: configured ? '#10b981' : 'var(--text-secondary, #888)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: isLive ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <i className={configured ? 'fas fa-check-circle' : 'fas fa-key'} />
                  {configured ? 'Configured' : 'Configure'}
                </button>

                {/* Toggle Switch - disabled unless configured */}
                <button
                  onClick={() => !isLive && configured && handleToggle(platform.id)}
                  disabled={isLive || !configured}
                  title={!configured ? 'Configure stream key first' : (platform.enabled ? 'Disable platform' : 'Enable platform')}
                  style={{
                    width: '52px',
                    height: '28px',
                    borderRadius: '14px',
                    border: 'none',
                    background: platform.enabled
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'var(--bg-tertiary, #2a2a3e)',
                    cursor: (isLive || !configured) ? 'not-allowed' : 'pointer',
                    position: 'relative',
                    transition: 'background 0.2s ease',
                    flexShrink: 0,
                    opacity: (isLive || !configured) ? 0.4 : 1,
                  }}
                >
                  <div
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: 'white',
                      position: 'absolute',
                      top: '3px',
                      left: platform.enabled ? '27px' : '3px',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      {editModal.isOpen && editModal.platform && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
          onClick={() => setEditModal({ isOpen: false, platform: null })}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #1e1e2e 0%, #16161f 100%)',
              borderRadius: '20px',
              padding: '32px',
              width: '100%',
              maxWidth: '520px',
              margin: '20px',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: getPlatformColor(editModal.platform.id),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className={editModal.platform.icon} style={{ color: 'white', fontSize: '22px' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: '#fff' }}>
                  Configure {editModal.platform.name}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#888' }}>
                  Enter your streaming credentials
                </p>
              </div>
            </div>

            {/* RTMP URL Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '10px',
                fontSize: '15px',
                fontWeight: 600,
                color: '#fff',
                letterSpacing: '0.3px',
              }}>
                RTMP URL
              </label>
              <input
                type="text"
                value={editForm.rtmpUrl}
                onChange={(e) => setEditForm({ ...editForm, rtmpUrl: e.target.value })}
                placeholder="rtmp://..."
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  fontSize: '15px',
                  color: '#fff',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = 'rgba(109, 26, 134, 0.6)'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
              />
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666', lineHeight: '1.5' }}>
                {editModal.platform.id === 'youtube' && 'Usually: rtmp://a.rtmp.youtube.com/live2'}
                {editModal.platform.id === 'twitch' && 'Get your ingest server from stream.twitch.tv/ingests'}
                {editModal.platform.id === 'facebook' && 'Usually: rtmps://live-api-s.facebook.com:443/rtmp'}
                {editModal.platform.id === 'linkedin' && 'Usually: rtmps://fa.rtmp.linkedin.com:443/live'}
                {editModal.platform.id.startsWith('custom') && 'Enter your custom RTMP server URL'}
              </p>
            </div>

            {/* Stream Key Field */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block',
                marginBottom: '10px',
                fontSize: '15px',
                fontWeight: 600,
                color: '#fff',
                letterSpacing: '0.3px',
              }}>
                Stream Key
              </label>
              <input
                type="password"
                value={editForm.streamKey}
                onChange={(e) => setEditForm({ ...editForm, streamKey: e.target.value })}
                placeholder="Enter your stream key"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  fontSize: '15px',
                  color: '#fff',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = 'rgba(109, 26, 134, 0.6)'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
              />
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666', lineHeight: '1.5' }}>
                <i className="fas fa-lock" style={{ marginRight: '6px' }} />
                Keep this secret! Get it from your {editModal.platform.name} dashboard.
              </p>
            </div>

            {/* Help Section - Collapsible */}
            {(() => {
              const platformKey = editModal.platform.id.startsWith('custom') ? 'custom' : editModal.platform.id;
              const helpInfo = STREAM_KEY_HELP[platformKey];

              if (!helpInfo) return null;

              return (
                <div style={{ marginBottom: '28px' }}>
                  <button
                    onClick={() => setShowHelp(!showHelp)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'rgba(109, 26, 134, 0.15)',
                      border: '1px solid rgba(109, 26, 134, 0.3)',
                      borderRadius: '10px',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      color: '#b78ed3',
                      fontSize: '14px',
                      fontWeight: 500,
                      width: '100%',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'rgba(109, 26, 134, 0.25)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(109, 26, 134, 0.15)';
                    }}
                  >
                    <span>
                      <i className="fas fa-question-circle" style={{ marginRight: '8px' }} />
                      Need help finding your stream key?
                    </span>
                    <i className={`fas fa-chevron-${showHelp ? 'up' : 'down'}`} />
                  </button>

                  {showHelp && (
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '16px',
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <h4 style={{
                        margin: '0 0 12px 0',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <i className={editModal.platform.icon} style={{ color: getPlatformColor(editModal.platform.id) }} />
                        How to find your {editModal.platform.name} Stream Key
                      </h4>

                      <ol style={{
                        margin: '0 0 16px 0',
                        paddingLeft: '20px',
                        color: '#aaa',
                        fontSize: '13px',
                        lineHeight: '1.8',
                      }}>
                        {helpInfo.steps.map((step, index) => (
                          <li key={index} style={{ marginBottom: '6px' }}>{step}</li>
                        ))}
                      </ol>

                      {helpInfo.link && (
                        <a
                          href={helpInfo.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: '#b78ed3',
                            fontSize: '13px',
                            textDecoration: 'none',
                            fontWeight: 500,
                            transition: 'color 0.2s ease',
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.color = '#d4a5f0';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.color = '#b78ed3';
                          }}
                        >
                          <i className="fas fa-external-link-alt" />
                          Official {editModal.platform.name} Help Documentation
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '14px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditModal({ isOpen: false, platform: null })}
                style={{
                  padding: '14px 28px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                style={{
                  padding: '14px 32px',
                  background: 'linear-gradient(135deg, #6D1A86 0%, #4c0f5e 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 15px rgba(109, 26, 134, 0.4)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(109, 26, 134, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(109, 26, 134, 0.4)';
                }}
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformConfig;
